import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faServer } from "@fortawesome/pro-regular-svg-icons";
import { useSearchParams } from "react-router-dom";

import { ModuleKindSection } from "src/adminPanel/modulesV2/ModuleKindSection";
import {
  enrichModulesForSearch,
  getModuleKindDisplayName,
  MODULE_SEARCH_OPTIONS,
} from "src/adminPanel/modulesV2/utils";
import { useAllOrgs } from "src/adminPanel/queries";
import { SearchInput, useUrlSyncedSearch } from "src/adminPanel/SearchInput";
import { Module, useModulesByKind } from "src/api/modulesApi";
import { useWorkspaces } from "src/api/queries";
import { Checkbox } from "src/base-components/Checkbox";
import { Tabs } from "src/base-components/Tabs";
import { LifecycleState, ModuleKind } from "src/clients/flow-api";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { useFuseSearch } from "src/utils/useFuseSearch";

const allKinds: ModuleKind[] = Object.values(ModuleKind);

// A module is "inactive" only once its deployed spec has a lifecycle_state past ACTIVE. No deployed
// spec yet, or a spec without a known lifecycle_state, counts as active so the module stays visible.
const isInactiveModule = (module: Module): boolean =>
  module.spec?.lifecycle_state != null &&
  module.spec.lifecycle_state !== LifecycleState.ACTIVE;

export const ModulesV2Settings: React.FC = () => {
  const {
    byKind: grouped,
    allModules,
    isLoading,
    isError,
  } = useModulesByKind();
  const { data: workspaces } = useWorkspaces(true, true);
  const { data: organizations } = useAllOrgs();
  const [selectedKindTab, setSelectedKindTab] = useState<string>("");
  const [showInactiveModules, setShowInactiveModules] =
    useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { initialSearch, debouncedSearchTerm, handleDebouncedSearchChange } =
    useUrlSyncedSearch();

  const workspaceMap = useMemo(
    () =>
      new Map(
        workspaces?.map((ws) => [
          ws.id,
          { name: ws.name, orgId: ws.organization_id, slug: ws.slug },
        ]),
      ),
    [workspaces],
  );
  const orgMap = useMemo(
    () => new Map(organizations?.map((org) => [org.id, org.name])),
    [organizations],
  );

  const handleTabChange = useCallback(
    (tabKey: string) => {
      setSelectedKindTab(tabKey);
      setSearchParams(
        (prev) => {
          prev.set("kind", tabKey);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const urlType = searchParams.get("kind") || "";

    if (
      urlType &&
      allKinds.includes(urlType as ModuleKind) &&
      urlType !== selectedKindTab
    ) {
      setSelectedKindTab(urlType);
      return;
    }

    if (!selectedKindTab) {
      const defaultKind = allKinds[0];
      setSelectedKindTab(defaultKind);
      if (urlType !== defaultKind) {
        setSearchParams(
          (prev) => {
            prev.set("kind", defaultKind);
            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [searchParams, selectedKindTab, setSearchParams]);

  const agentWorkspaceIds = useMemo(
    () =>
      new Set(
        allModules
          .filter((m) => m.kind === ModuleKind.AGENTS)
          .map((m) => m.workspaceId),
      ),
    [allModules],
  );

  // Built from the full (unfiltered) per-kind list so a hidden inactive module still blocks
  // re-enabling its workspace in the Enable Module modal.
  const existingWorkspaceIdsByKind = useMemo(
    () =>
      Object.fromEntries(
        allKinds.map((kind) => [
          kind,
          new Set(grouped[kind].map((m) => m.workspaceId)),
        ]),
      ) as Record<ModuleKind, Set<string>>,
    [grouped],
  );

  // The list always fetches inactive modules; hide them unless the toggle is on. agentWorkspaceIds
  // above intentionally stays on the full set so an inactive agents module still blocks duplicates.
  const visibleModules = useMemo(
    () =>
      showInactiveModules
        ? allModules
        : allModules.filter((m) => !isInactiveModule(m)),
    [allModules, showInactiveModules],
  );
  const visibleByKind = useMemo(
    () =>
      Object.fromEntries(
        allKinds.map((kind) => [
          kind,
          showInactiveModules
            ? grouped[kind]
            : grouped[kind].filter((m) => !isInactiveModule(m)),
        ]),
      ) as Record<ModuleKind, Module[]>,
    [grouped, showInactiveModules],
  );

  const allSearchableModules = useMemo(
    () => enrichModulesForSearch(visibleModules, workspaceMap, orgMap),
    [visibleModules, workspaceMap, orgMap],
  );
  const fuseSearch = useFuseSearch(allSearchableModules, MODULE_SEARCH_OPTIONS);
  const searchResults = useMemo(
    () => fuseSearch(debouncedSearchTerm),
    [fuseSearch, debouncedSearchTerm],
  );
  const filteredCounts = useMemo(
    () =>
      Object.fromEntries(
        allKinds.map((kind) => [
          kind,
          searchResults.filter((mod) => mod.kind === kind).length,
        ]),
      ),
    [searchResults],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-screen-2xl">
          <div className="mb-8 space-y-2">
            <Skeleton height="h-8" width="w-70" />
            <Skeleton height="h-5" width="w-96" />
          </div>
          <Skeleton height="h-10" width="w-full" />
          <div className="mt-6 space-y-3">
            <Skeleton height="h-12" width="w-full" />
            <Skeleton height="h-12" width="w-full" />
            <Skeleton height="h-12" width="w-full" />
            <Skeleton height="h-12" width="w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-screen-2xl">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <EmptyState
              description="Failed to load modules. Please try again."
              headline="Error loading modules"
              icon={faServer}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-8">
          <h1 className="font-inter-semibold-32px text-gray-900">Modules</h1>
          <p className="mt-2 font-inter-normal-16px text-gray-600">
            Manage and monitor modules with operation tracking.
          </p>
        </div>

        <div className="mb-6">
          <SearchInput
            initialValue={initialSearch}
            placeholder="Search by organization, workspace, module ID or active operation ID..."
            onDebouncedChange={handleDebouncedSearchChange}
          />
          <label className="mt-3 flex w-fit cursor-pointer items-center gap-x-2 font-inter-normal-13px text-gray-600">
            <Checkbox
              checked={showInactiveModules}
              dataLoc="modules-show-inactive-toggle"
              onChange={() => setShowInactiveModules((prev) => !prev)}
            />
            Show inactive modules
          </label>
        </div>

        <Tabs
          containerClassName="bg-white rounded-xl border border-gray-200 shadow-sm"
          panelsClassName="p-6"
          selectedKey={selectedKindTab || allKinds[0]}
          tabClassName="border-b border-gray-200 px-6"
          tabs={allKinds.map((kind) => ({
            key: kind,
            label: (
              <div className="flex items-center gap-2">
                <span>{getModuleKindDisplayName(kind)}</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 font-inter-normal-12px text-gray-500">
                  {debouncedSearchTerm
                    ? `${filteredCounts[kind]}/${visibleByKind[kind].length}`
                    : visibleByKind[kind].length}
                </span>
              </div>
            ),
            content: (
              <ModuleKindSection
                agentWorkspaceIds={agentWorkspaceIds}
                existingWorkspaceIds={existingWorkspaceIdsByKind[kind]}
                moduleKind={kind}
                modules={visibleByKind[kind]}
                orgMap={orgMap}
                searchTerm={debouncedSearchTerm}
                workspaceMap={workspaceMap}
                workspaces={workspaces ?? []}
              />
            ),
          }))}
          onChange={handleTabChange}
        />
      </div>
    </div>
  );
};
